<template>
	<div>
		<div v-if="singleTextRecommendation">
			<div class="text-recommendation-container">
				<TextRecommendationCardTitleDetail
					:textRecommendation="textRecommendations[0]"
					:isSingleTextRecommendation="singleTextRecommendation"
					:title="title"
					:hideMedia="true"
					:additionalContexts="additionalContexts"
				/>
			</div>
		</div>

		<div class="text-recommendations" v-else-if="textRecommendations?.length > 0">
			<Top10TitleSwiper
				class="mobile"
				:titleCount="textRecommendations?.length"
				:showSwappers="false"
				:showTitle="false"
				:showBullets="false"
				:loop="false"
			>
				<div
					class="swiper-slide custom-slide"
					v-for="textRecommendation in textRecommendations"
					:key="textRecommendation.id"
				>
					<div class="text-recommendation-container">
						<TextRecommendationCardTitleDetail
							:textRecommendation="textRecommendation"
							:isSingleTextRecommendation="singleTextRecommendation"
							:title="title"
							:hideMedia="true"
							:additionalContexts="additionalContexts"
						/>
					</div>
				</div>
			</Top10TitleSwiper>

			<HiddenHorizontalScrollbar class="desktop">
				<div
					class="text-recommendation-container"
					v-for="textRecommendation in textRecommendations"
					:key="textRecommendation.id"
				>
					<TextRecommendationCardTitleDetail
						:textRecommendation="textRecommendation"
						:isSingleTextRecommendation="singleTextRecommendation"
						:title="title"
						:hideMedia="true"
						:additionalContexts="additionalContexts"
					/>
				</div>
			</HiddenHorizontalScrollbar>
		</div>

		<div v-else-if="showTextRecommendationTools">
			<TextRecommendationEmpty :title="title" />
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import TextRecommendationEmpty from '@/features/why-to-watch/components/TextRecommendationEmpty.vue';
import Top10TitleSwiper from '@/components/popular/provider-module/Top10TitleSwiper.vue';
import TextRecommendationCardTitleDetail from '@/features/why-to-watch/components/TextRecommendationCardTitleDetail.vue';

import { TitleDetail } from '@/interfaces/title-details-graphql';

import {
	GetTextRecommendationsDocument,
	GetTextRecommendationsQuery,
	GetTextRecommendationsQueryVariables,
} from '@/features/why-to-watch/graphql/queries/GetTextRecommendations.query';

import { SnowplowContext } from '@/helpers/tracking/providers';

import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';

const props = defineProps<{
	title: TitleDetail;
	additionalContexts: SnowplowContext[];
}>();

const { language, country } = storeToRefs(useLanguageStore());
const { isAdmin, isEditor } = storeToRefs(useUserStore());

const { result } = useQuery<GetTextRecommendationsQuery, GetTextRecommendationsQueryVariables>(
	GetTextRecommendationsDocument,
	() => ({
		language: language.value,
		country: country.value,
		jwEntityId: props.title.id,
	}),
	{
		errorPolicy: 'all',
	}
);

const textRecommendations = computed(() => result.value?.textRecommendations ?? []);

const singleTextRecommendation = computed(() => textRecommendations.value.length === 1);

const showTextRecommendationTools = computed(() => isAdmin.value || isEditor.value);
</script>

<style lang="scss" scoped>
.text-recommendations {
	overflow: hidden;
	margin-inline-end: -16px;
}

.text-recommendation-container {
	width: 368px;
	margin-inline-end: 16px;
	display: flex;
	align-self: start;

	@media (max-width: 512px) {
		width: 100%;
		margin-inline-end: 0;
	}
}

.desktop {
	display: none !important;

	@media (min-width: 512px) {
		display: block !important;
	}
}

.mobile {
	display: block !important;
	border-radius: 8px;
	width: calc(100vw - 24px) !important;
	overflow: visible !important;
	background: transparent !important;

	@media (min-width: 512px) {
		display: none !important;
	}
}

.custom-slide {
	width: 360px !important;

	@media (max-width: 512px) {
		width: calc(100vw - 24px) !important;
	}
}
</style>
